import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { Navigate, Route, Routes } from "react-router-dom";
import { isAdminNavRole } from "./_lib/epocus-role-variant";
import { EpocusDatabaseGate } from "./_lib/local-storage-unavailable";
import { ProgramSetupPending } from "./_lib/program-setup-pending";
import { AccessDenied } from "./_lib/rbac/access-denied";
import type { Capability } from "./_lib/rbac/capabilities";
import {
  useEpocusRole,
  useIsEpocusPlatformAdmin,
} from "./_lib/rbac/epocus-claim";
import { EpocusClaimProvider } from "./_lib/rbac/epocus-claim-provider";
import { RoleGate } from "./_lib/rbac/role-gate";
import { useHasEpocusProgram } from "./_lib/use-has-epocus-program";
import { EpocusAdminLayout } from "./admin/layout";
import { linkToEpocusAdmin } from "./admin/links";
import { EpocusAdminProgramsPage } from "./admin/page";
import { EpocusAdminProgramPage } from "./admin/programs/[:programId]/page";
import { EpocusAdminAddProgramUserPage } from "./admin/programs/[:programId]/users/new/page";
import { EpocusBillingPage } from "./billing/page";
import { EpocusBillingReportPage } from "./billing/reports/[:examId]/page";
import { EpocusExamPage } from "./encounters/[:encounterId]/exams/[:examId]/page";
import { EpocusAddExamPage } from "./encounters/[:encounterId]/exams/new/page";
import { EpocusEncounterPage } from "./encounters/[:encounterId]/page";
import { EpocusNewEncounterPage } from "./encounters/new/page";
import { EpocusHomePage } from "./home/page";
import { EpocusLayout } from "./layout";
import { linkToEpocusOnboarding } from "./links";
import { EpocusMePage } from "./me/page";
import { EpocusOnboardingLayout } from "./onboarding/layout";
import { EpocusOnboardingPage } from "./onboarding/page";
import { EpocusCredentialingPage } from "./program/credentialing/page";
import { EpocusProgramPage } from "./program/page";
import { EpocusPerformerDetailPage } from "./program/performers/[:performerId]/page";
import { EpocusQaWorkloadPage } from "./program/qa-workload/page";
import { EpocusReferralDetailPage } from "./referrals/[:referralId]/page";
import { EpocusReferralsInboxPage } from "./referrals/inbox/page";
import { EpocusReviewPanelPage } from "./review/[:examId]/page";
import { EpocusReviewEncounterPage } from "./review/encounter/[:encounterId]/page";
import { EpocusReviewPage } from "./review/page";
import { EpocusAuditTrailPage } from "./settings/audit/page";
import { EpocusSettingsPage } from "./settings/page";
import { EpocusProgramConfigPage } from "./settings/program/page";
import { EpocusEditUserPage } from "./settings/users/[:userId]/edit/page";
import { EpocusAddUserPage } from "./settings/users/new/page";
import { EpocusManageUsersPage } from "./settings/users/page";

/**
 * Route-boundary RBAC guard. Renders `<AccessDenied />` rather than silent-hiding
 * so a deep link to a route the user can't reach explains itself instead of
 * rendering blank. In-page controls use bare `<RoleGate>` (no fallback) instead.
 */
const Guarded: FCwC<{ can: Capability }> = ({ can, children }) => (
  <RoleGate can={can} fallback={<AccessDenied capability={can} />}>
    {children}
  </RoleGate>
);

const ReferralsGate: FCwC = ({ children }) => {
  const [enabled, { loading }] = useFeatureFlag(featureFlags.epocus.referrals);
  // Decide only once flags have loaded — the initial empty flag set reads as
  // off, which would redirect even users who have referrals enabled.
  if (loading) return null;
  return enabled ? <>{children}</> : <Navigate to="/epocus/home" replace />;
};

const OnboardingGate: FCwC = ({ children }) => {
  const [enabled, { loading }] = useFeatureFlag(
    featureFlags.epocus.hackathonSept26,
  );
  if (loading) return null;
  return enabled ? <>{children}</> : <Navigate to="/epocus/home" replace />;
};

const ProgramMembershipGate: FCwC = ({ children }) => {
  const [onboardingEnabled, { loading: flagLoading }] = useFeatureFlag(
    featureFlags.epocus.hackathonSept26,
  );
  const { hasProgram, loading } = useHasEpocusProgram();
  const isEpocusAdmin = useIsEpocusPlatformAdmin();

  if (isEpocusAdmin) return <Navigate to={linkToEpocusAdmin()} replace />;

  if (flagLoading) return null;

  if (onboardingEnabled) {
    if (loading) return null;
    return hasProgram ? (
      <>{children}</>
    ) : (
      <Navigate to={linkToEpocusOnboarding()} replace />
    );
  }

  return !loading && !hasProgram ? <ProgramSetupPending /> : <>{children}</>;
};

const EpocusIndexRedirect: FC = () => {
  const isEpocusAdmin = useIsEpocusPlatformAdmin();
  const role = useEpocusRole();

  if (isEpocusAdmin) return <Navigate to="admin" replace />;
  return <Navigate to={isAdminNavRole(role) ? "program" : "home"} replace />;
};

export const EpocusRoutes: FC = () => (
  <EpocusClaimProvider>
    <EpocusDatabaseGate>
      <Routes>
        <Route path="admin" element={<EpocusAdminLayout />}>
          <Route index element={<EpocusAdminProgramsPage />} />
          <Route
            path="programs/:programId"
            element={<EpocusAdminProgramPage />}
          />
          <Route
            path="programs/:programId/users/new"
            element={<EpocusAdminAddProgramUserPage />}
          />
        </Route>
        <Route
          path="onboarding"
          element={
            <OnboardingGate>
              <EpocusOnboardingLayout />
            </OnboardingGate>
          }
        >
          <Route index element={<EpocusOnboardingPage />} />
        </Route>
        <Route index element={<EpocusIndexRedirect />} />
        <Route
          element={
            <ProgramMembershipGate>
              <EpocusLayout />
            </ProgramMembershipGate>
          }
        >
          <Route path="home" element={<EpocusHomePage />} />
          <Route path="me" element={<EpocusMePage />} />
          <Route
            path="program"
            element={
              <Guarded can="viewProgramDashboard">
                <EpocusProgramPage />
              </Guarded>
            }
          />
          <Route
            path="program/credentialing"
            element={
              <Guarded can="viewCredentialing">
                <EpocusCredentialingPage />
              </Guarded>
            }
          />
          <Route
            path="program/qa-workload"
            element={
              <Guarded can="viewQaWorkload">
                <EpocusQaWorkloadPage />
              </Guarded>
            }
          />
          <Route
            path="program/performers/:performerId"
            element={
              <Guarded can="viewProgramDashboard">
                <EpocusPerformerDetailPage />
              </Guarded>
            }
          />
          <Route
            path="billing"
            element={
              <Guarded can="manageBilling">
                <EpocusBillingPage />
              </Guarded>
            }
          />
          <Route
            path="billing/reports/:examId"
            element={
              <Guarded can="manageBilling">
                <EpocusBillingReportPage />
              </Guarded>
            }
          />
          <Route path="settings" element={<EpocusSettingsPage />} />
          <Route
            path="settings/program"
            element={<EpocusProgramConfigPage />}
          />
          <Route
            path="settings/audit"
            element={
              <OnboardingGate>
                <EpocusAuditTrailPage />
              </OnboardingGate>
            }
          />
          <Route path="settings/users" element={<EpocusManageUsersPage />} />
          <Route path="settings/users/new" element={<EpocusAddUserPage />} />
          <Route
            path="settings/users/:userId/edit"
            element={<EpocusEditUserPage />}
          />
          <Route
            path="review"
            element={
              <Guarded can="viewReviewQueue">
                <EpocusReviewPage />
              </Guarded>
            }
          />
          <Route
            path="review/encounter/:encounterId"
            element={
              <Guarded can="viewReviewQueue">
                <EpocusReviewEncounterPage />
              </Guarded>
            }
          />
          <Route
            path="review/:examId"
            element={
              <Guarded can="submitReview">
                <EpocusReviewPanelPage />
              </Guarded>
            }
          />
          <Route
            path="referrals/inbox"
            element={
              <ReferralsGate>
                <EpocusReferralsInboxPage />
              </ReferralsGate>
            }
          />
          <Route
            path="referrals/:referralId"
            element={
              <ReferralsGate>
                <EpocusReferralDetailPage />
              </ReferralsGate>
            }
          />
          <Route
            path="encounters/new"
            element={
              <Guarded can="createEncounter">
                <EpocusNewEncounterPage />
              </Guarded>
            }
          />
          {/* Detail routes are deliberately ungated — reviewers and program
          oversight need read access. The read/edit split is enforced by the
          worksheet's `readOnly` mode and the in-page action gates (PNE-533). */}
          <Route
            path="encounters/:encounterId"
            element={<EpocusEncounterPage />}
          />
          <Route
            path="encounters/:encounterId/exams/new"
            element={
              <Guarded can="createEncounter">
                <EpocusAddExamPage />
              </Guarded>
            }
          />
          <Route
            path="encounters/:encounterId/exams/:examId"
            element={<EpocusExamPage />}
          />
        </Route>
      </Routes>
    </EpocusDatabaseGate>
  </EpocusClaimProvider>
);
